import { useEffect, useState } from "react";
import { useAuth } from "@/components/providers/AuthProvider";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClipboardList, CheckCircle2, XCircle, Package2, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Product {
  id: string;
  name: string;
  code: string;
  unit: string;
  quantity: number;
  min_quantity: number;
}

interface OrderItem {
  id: string;
  quantity: number;
  product: Product;
}

interface Order {
  id: string;
  order_number: string;
  created_at: string;
  raw_date: Date;
  status: string;
  construction_name: string;
  order_items: OrderItem[];
}

interface DashboardData {
  totalOrders: number;
  approvedOrders: number;
  rejectedOrders: number;
  totalItemsReceived: number;
  recentApprovedOrders: Order[];
  recentPendingOrders: Order[];
  totalProducts: number;
  lowStockCount: number;
}

export default function UserDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalOrders: 0,
    approvedOrders: 0,
    rejectedOrders: 0,
    totalItemsReceived: 0,
    recentApprovedOrders: [],
    recentPendingOrders: [],
    totalProducts: 0,
    lowStockCount: 0
  });
  const [selectedMonth, setSelectedMonth] = useState<string>(
    new Date().toISOString().slice(0, 7)
  ); // Formato: "YYYY-MM"
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  useEffect(() => {
    // Atualiza as estatísticas quando o mês selecionado muda
    updateDashboardStats();
  }, [selectedMonth, allOrders]);

  const loadDashboardData = async () => {
    try {
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      // Buscar produtos com estoque baixo
      const { data: lowStockProducts, error: productsError } = await supabase
        .from('products')
        .select('*')
        .filter('quantity', 'lt', 'min_quantity');

      if (productsError) {
        console.error('Erro ao buscar produtos:', productsError);
        throw productsError;
      }

      // Buscar pedidos com itens e produtos
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          created_at,
          status,
          construction_name,
          department_id,
          order_items (
            id,
            quantity,
            product:products (
              id,
              name,
              code,
              unit,
              quantity,
              min_quantity
            )
          )
        `);

      if (ordersError) {
        console.error('Erro ao carregar pedidos:', ordersError);
        throw ordersError;
      }

      // Formatar os pedidos com as datas
      const formattedOrders = (ordersData || []).map(order => ({
        ...order,
        raw_date: new Date(order.created_at),
        order_items: order.order_items || []
      }));

      // Atualizar o estado com os dados
      setDashboardData(prevData => ({
        ...prevData,
        lowStockCount: lowStockProducts?.length || 0
      }));

      setAllOrders(formattedOrders);
      updateDashboardStats(formattedOrders);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados do dashboard",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDashboardStats = (orders = allOrders) => {
    // Filtrar pedidos pelo mês selecionado
    const [year, month] = selectedMonth.split('-').map(Number);
    const filteredOrders = orders.filter(order => {
      const orderDate = order.raw_date;
      return orderDate.getFullYear() === year && orderDate.getMonth() === month - 1;
    });

    // Calcular o total de itens recebidos (soma das quantidades dos itens em pedidos aprovados)
    const totalItemsReceived = filteredOrders
      .filter(order => order.status === 'approved')
      .reduce((total, order) => {
        return total + (order.order_items?.reduce((itemTotal, item) => itemTotal + item.quantity, 0) || 0);
      }, 0);

    // Calcular as estatísticas com os pedidos filtrados
    const stats = {
      totalOrders: filteredOrders.length,
      approvedOrders: filteredOrders.filter(order => order.status === 'approved').length,
      rejectedOrders: filteredOrders.filter(order => order.status === 'rejected').length,
      totalItemsReceived,
      recentApprovedOrders: filteredOrders
        .filter(order => order.status === 'approved')
        .sort((a, b) => b.raw_date.getTime() - a.raw_date.getTime())
        .slice(0, 5),
      recentPendingOrders: filteredOrders
        .filter(order => order.status === 'pending')
        .sort((a, b) => b.raw_date.getTime() - a.raw_date.getTime())
        .slice(0, 5)
    };

    setDashboardData(prevData => ({
      ...prevData,
      ...stats
    }));
  };

  const fetchRecentOrders = async () => {
    try {
      console.log('Iniciando busca de pedidos recentes...');

      // Buscar pedidos aprovados recentes
      const { data: approvedOrdersData, error: approvedOrdersError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          created_at,
          status,
          responsible_name,
          construction_name,
          user_id,
          department_id
        `)
        .eq('status', 'approved')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (approvedOrdersError) {
        console.error('Erro ao buscar pedidos aprovados:', approvedOrdersError);
        throw approvedOrdersError;
      }

      // Buscar pedidos pendentes recentes
      const { data: pendingOrdersData, error: pendingOrdersError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          created_at,
          status,
          responsible_name,
          construction_name,
          user_id,
          department_id
        `)
        .eq('status', 'pending')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (pendingOrdersError) {
        console.error('Erro ao buscar pedidos pendentes:', pendingOrdersError);
        throw pendingOrdersError;
      }

      // Atualizar o estado com os pedidos encontrados
      setDashboardData(prevData => ({
        ...prevData,
        recentApprovedOrders: approvedOrdersData || [],
        recentPendingOrders: pendingOrdersData || []
      }));

      console.log('Pedidos aprovados encontrados:', approvedOrdersData);
      console.log('Pedidos pendentes encontrados:', pendingOrdersData);
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
      toast({
        title: "Erro ao carregar pedidos",
        description: "Ocorreu um erro ao carregar os pedidos. Tente novamente mais tarde.",
        variant: "destructive",
      });
    }
  };

  const fetchDashboardData = async () => {
    try {
      const currentDate = new Date();
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      // Buscar total de produtos únicos
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id');

      if (productsError) {
        console.error('Erro ao buscar produtos:', productsError);
        return;
      }

      // Buscar estatísticas de pedidos
      const { data: orderStats, error: orderStatsError } = await supabase
        .from('orders')
        .select('status')
        .eq('user_id', user?.id)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (orderStatsError) {
        console.error('Erro ao buscar estatísticas:', orderStatsError);
        return;
      }

      // Buscar pedidos pendentes recentes
      const { data: pendingOrders, error: pendingError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          created_at,
          status,
          responsible_name,
          construction_name,
          user_id,
          department:departments (
            id,
            name,
            acronym
          )
        `)
        .eq('user_id', user?.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(5);

      if (pendingError) {
        console.error('Erro ao buscar pedidos pendentes:', pendingError);
      }

      // Buscar pedidos aprovados recentes
      const { data: approvedOrders, error: approvedError } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          created_at,
          status,
          responsible_name,
          construction_name,
          user_id,
          department:departments (
            id,
            name,
            acronym
          )
        `)
        .eq('user_id', user?.id)
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(5);

      if (approvedError) {
        console.error('Erro ao buscar pedidos aprovados:', approvedError);
      }

      // Buscar itens dos pedidos pendentes e seus produtos
      const { data: pendingItemsData, error: pendingItemsError } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', (pendingOrders || []).map(order => order.id));

      if (pendingItemsError) {
        console.error('Erro ao buscar itens dos pedidos pendentes:', pendingItemsError);
      }

      // Buscar produtos dos itens pendentes
      const { data: pendingProductsData, error: pendingProductsError } = await supabase
        .from('products')
        .select('*')
        .in('id', (pendingItemsData || []).map(item => item.product_id));

      if (pendingProductsError) {
        console.error('Erro ao buscar produtos dos pedidos pendentes:', pendingProductsError);
      }

      // Buscar itens dos pedidos aprovados e seus produtos
      const { data: approvedItemsData, error: approvedItemsError } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', (approvedOrders || []).map(order => order.id));

      if (approvedItemsError) {
        console.error('Erro ao buscar itens dos pedidos aprovados:', approvedItemsError);
      }

      // Buscar produtos dos itens aprovados
      const { data: approvedProductsData, error: approvedProductsError } = await supabase
        .from('products')
        .select('*')
        .in('id', (approvedItemsData || []).map(item => item.product_id));

      if (approvedProductsError) {
        console.error('Erro ao buscar produtos dos pedidos aprovados:', approvedProductsError);
      }

      // Transformar pedidos pendentes
      const transformedPendingOrders = (pendingOrders || []).map(order => {
        const orderItems = (pendingItemsData || [])
          .filter(item => item.order_id === order.id)
          .map(item => {
            const product = pendingProductsData?.find(p => p.id === item.product_id);
            return {
              id: item.id,
              quantity: item.quantity,
              product: product || null
            };
          });

        return {
          ...order,
          order_items: orderItems
        };
      });

      // Transformar pedidos aprovados
      const transformedApprovedOrders = (approvedOrders || []).map(order => {
        const orderItems = (approvedItemsData || [])
          .filter(item => item.order_id === order.id)
          .map(item => {
            const product = approvedProductsData?.find(p => p.id === item.product_id);
            return {
              id: item.id,
              quantity: item.quantity,
              product: product || null
            };
          });

        return {
          ...order,
          order_items: orderItems
        };
      });

      // Calcular estatísticas
      const stats = {
        totalProducts: productsData?.length || 0,
        approvedOrders: orderStats?.filter(order => order.status === 'approved').length || 0,
        pendingOrders: orderStats?.filter(order => order.status === 'pending').length || 0,
        rejectedOrders: orderStats?.filter(order => order.status === 'rejected').length || 0,
        recentApprovedOrders: transformedApprovedOrders,
        recentPendingOrders: transformedPendingOrders
      };

      setDashboardData(stats);
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
    }
  };

  useEffect(() => {
    fetchRecentOrders();
  }, []);

  // Gerar lista de meses para o filtro (últimos 12 meses)
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    
    return options;
  };

  // Formatar o mês selecionado para exibição
  const formatSelectedMonth = () => {
    const [year, month] = selectedMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center flex-col sm:flex-row gap-4">
        <h1 className="text-3xl font-bold">Painel de Pedidos</h1>
        <div className="flex flex-col sm:flex-row gap-4">
          <Select
            value={selectedMonth}
            onValueChange={setSelectedMonth}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Selecione o mês" />
            </SelectTrigger>
            <SelectContent>
              {getMonthOptions().map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => navigate('/orders/new')}>
            <Package2 className="w-4 h-4 mr-2" />
            Novo Pedido
          </Button>
          <Button variant="outline" onClick={() => navigate('/my-orders')}>
            <ClipboardList className="w-4 h-4 mr-2" />
            Meus Pedidos
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {/* Card - Total de Pedidos */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium">Total de Pedidos</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{formatSelectedMonth()}</p>
            </div>
            <ClipboardList className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{dashboardData.totalOrders}</div>
          </CardContent>
        </Card>

        {/* Card - Pedidos Aprovados */}
        <Card className="bg-green-50 border-green-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium">Pedidos Aprovados</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{formatSelectedMonth()}</p>
            </div>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{dashboardData.approvedOrders}</div>
          </CardContent>
        </Card>

        {/* Card - Estoque Baixo */}
        <Card className="bg-yellow-50 border-yellow-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium">Estoque Baixo</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Produtos abaixo do mínimo</p>
            </div>
            <Package2 className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{dashboardData.lowStockCount}</div>
          </CardContent>
        </Card>

        {/* Card - Pedidos Rejeitados */}
        <Card className="bg-red-50 border-red-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-sm font-medium">Pedidos Rejeitados</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{formatSelectedMonth()}</p>
            </div>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{dashboardData.rejectedOrders}</div>
          </CardContent>
        </Card>
      </div>

      {/* Card - Pedidos Aprovados Recentes */}
      <Card className="bg-white">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-xl font-semibold">Pedidos Aprovados Recentes</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Últimos 5 pedidos aprovados em {formatSelectedMonth()}
            </p>
          </div>
          <Calendar className="h-5 w-5 text-gray-500" />
        </CardHeader>
        <CardContent>
          {dashboardData.recentApprovedOrders.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Quantidade</TableHead>
                  <TableHead>Obra</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboardData.recentApprovedOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      {new Date(order.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>
                      {order.order_items?.map((item, index) => (
                        <div key={index}>
                          {item.product.name}
                          {index < order.order_items.length - 1 && <br />}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>
                      {order.order_items?.map((item, index) => (
                        <div key={index}>
                          {item.quantity} unidades
                          {index < order.order_items.length - 1 && <br />}
                        </div>
                      ))}
                    </TableCell>
                    <TableCell>{order.construction_name}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        Ver Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6 text-gray-500">
              Nenhum pedido aprovado no período selecionado
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card de Pedidos Pendentes Recentes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Pedidos Pendentes Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {dashboardData.recentPendingOrders.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center">
                Nenhum pedido pendente encontrado
              </p>
            ) : (
              <div className="space-y-4">
                {dashboardData.recentPendingOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Pedido #{order.order_number}</p>
                      <p className="text-sm text-muted-foreground">{order.construction_name}</p>
                    </div>
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                      Pendente
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}